import { createSlice, PayloadAction } from '@reduxjs/toolkit';

import { AppThunk } from '../../../app/store';
import {
  deleteNoteEndPoint,
  getNotesEndPoint,
} from '../../../common/api.endPoints';
import { HttpService } from '../../../common/http.service';
import { INote } from '../interfaces/INote';

export const notesSlice = createSlice({
  name: 'notes',

  initialState: {
    notes: [] as INote[],
    createNoteFailed: false,
    createNoteError: '',
    createNoteErrorType: '',
    fetchNotesFailed: false,
    fetchNotesError: '',
    fetchNotesErrorType: '',
    deleteNoteFailed: false,
    deleteNoteError: '',
    deleteNoteErrorType: '',
  },

  reducers: {
    onNotesFetch: (state, action) => {
      state.notes = action.payload.notes;
    },
    onNotesFetchFailed: (state, action) => {
      state.fetchNotesFailed = true;
      state.fetchNotesError = action.payload.message;
      state.fetchNotesErrorType = action.payload.type;
    },
    onNoteCreate: (state, action) => {
      state.notes.push(action.payload.note);
    },
    onNoteCreateFailed: (state, action) => {
      state.createNoteFailed = true;
      state.createNoteError = action.payload.message;
      state.createNoteErrorType = action.payload.type;
    },
    onDeleteNote: (state, action: PayloadAction<{ noteId: string }>) => {
      const noteId: number = parseInt(action.payload.noteId, 10);
      state.notes = state.notes.filter(note => note.id !== noteId);
    },
    onDeleteNoteFailed: (
      state,
      action: PayloadAction<{ message: string; type: string }>,
    ) => {
      state.deleteNoteFailed = true;
      state.deleteNoteError = action.payload.message;
      state.deleteNoteErrorType = action.payload.type;
    },
  },
});

const transformNote = (data: any): INote => {
  const note: INote = {
    id: data.noteId,
    userId: data.userId,
    userName: data.author,
    date: data.createdAt,
    note: data.note,
    mibCodes: Array.isArray(data.mibCodes) ? data.mibCodes : [],
  };

  return note;
};

export const getNotes =
  (applicationId: string): AppThunk =>
  async dispatch => {
    const response = await HttpService.getInstance().get(
      getNotesEndPoint(applicationId),
    );

    if (response && !response.error) {
      const rawNotes = response.parsedBody;
      const notes: INote[] = rawNotes.map((rawNote: any) => {
        return transformNote(rawNote);
      });
      dispatch(notesActions.onNotesFetch({ notes }));
    } else {
      dispatch(
        notesActions.onNotesFetchFailed(response ? response.error : undefined),
      );
    }
  };

export const postNote =
  (
    applicationId: string,
    note: string,
    mibCodes: string[] = [],
  ): AppThunk<Promise<boolean>> =>
  async dispatch => {
    const requestBody = { note, mibCodes };
    const response = await HttpService.getInstance().post(
      getNotesEndPoint(applicationId),
      requestBody,
    );

    if (response && !response.error) {
      const updatedNote = response.parsedBody;
      const transformedNote = transformNote(updatedNote);
      dispatch(notesActions.onNoteCreate({ note: transformedNote }));
      return true;
    }

    dispatch(
      notesActions.onNoteCreateFailed(response ? response.error : undefined),
    );
    return false;
  };

export const deleteNote =
  (applicationId: string, noteId: string): AppThunk =>
  async dispatch => {
    try {
      const endpoint = deleteNoteEndPoint(applicationId, noteId);

      const response = await HttpService.getInstance().patch(endpoint);

      if (response && !response.error) {
        dispatch(notesActions.onDeleteNote({ noteId }));
      } else {
        dispatch(
          notesActions.onDeleteNoteFailed(
            response ? response.error : undefined,
          ),
        );
      }
    } catch (error: any) {
      console.error('Error in deleteNote:', error.message);
    }
  };

export const notesReducer = notesSlice.reducer;
export const notesActions = notesSlice.actions;
